import React, { useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import GithubIcon from "src/components/ui/icons/github";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { WarningAlert } from "src/components/ui/status-alert";
import { useToast } from "src/hooks/use-toast";

import { BuildkitePipelineTypeahead } from "../components/BuildkitePipelineTypeahead";
import { GithubOrgTypeahead } from "../components/GithubOrgTypeahead";
import { GithubRepoTypeahead } from "../components/GithubRepoTypeahead";
import { GithubWorkflowTypeahead } from "../components/GithubWorkflowTypeahead";
import {
  type BuildkitePipeline,
  CAPABILITY_TYPE_LABELS,
  type CapabilityTypeOption,
  type GithubRepo,
  type GithubWorkflow,
  type Parameter,
  type WizardState,
} from "../types";

const listGithubRepositoriesQuery = graphql`
  query Step1SelectWorkflowListGithubRepositoriesQuery($integrationId: GlobalID!) {
    listGithubRepositories(integrationId: $integrationId)
  }
`;

const listGithubWorkflowsQuery = graphql`
  query Step1SelectWorkflowListGithubWorkflowsQuery($integrationId: GlobalID!, $repoFullName: String!) {
    listGithubWorkflows(integrationId: $integrationId, repoFullName: $repoFullName)
  }
`;

const getGithubWorkflowInputsQuery = graphql`
  query Step1SelectWorkflowGetGithubWorkflowInputsQuery(
    $integrationId: GlobalID!
    $repoFullName: String!
    $workflowId: Int!
  ) {
    getGithubWorkflowInputs(integrationId: $integrationId, repoFullName: $repoFullName, workflowId: $workflowId)
  }
`;

const listBuildkitePipelinesQuery = graphql`
  query Step1SelectWorkflowListBuildkitePipelinesQuery($integrationId: GlobalID!) {
    listBuildkitePipelines(integrationId: $integrationId)
  }
`;

const getBuildkitePipelineInputsQuery = graphql`
  query Step1SelectWorkflowGetBuildkitePipelineInputsQuery(
    $integrationId: GlobalID!
    $orgSlug: String!
    $pipelineSlug: String!
  ) {
    getBuildkitePipelineInputs(integrationId: $integrationId, orgSlug: $orgSlug, pipelineSlug: $pipelineSlug)
  }
`;

interface IntegrationNode {
  id: string;
  name: string;
  integrationType: string;
  githubAccountName: string | null | undefined;
  buildkiteOrgSlug: string | null | undefined;
}

interface Step1SelectWorkflowProps {
  state: WizardState;
  onUpdate: (updates: Partial<WizardState>) => void;
  githubIntegrations: IntegrationNode[];
  buildkiteIntegrations: IntegrationNode[];
}

export function Step1SelectWorkflow({
  state,
  onUpdate,
  githubIntegrations,
  buildkiteIntegrations,
}: Step1SelectWorkflowProps) {
  const environment = useRelayEnvironment();
  const { toast } = useToast();

  // Track loading states separately for better UX
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  // Get selected integration
  const selectedGithubIntegration = React.useMemo(() => {
    if (state.selectedGitHubOrgId) {
      return githubIntegrations.find((integration) => integration.id === state.selectedGitHubOrgId);
    }
    return githubIntegrations.length === 1 ? githubIntegrations[0] : undefined;
  }, [githubIntegrations, state.selectedGitHubOrgId]);

  const selectedBuildkiteIntegration = React.useMemo(() => {
    if (state.selectedBuildkiteOrgId) {
      return buildkiteIntegrations.find((integration) => integration.id === state.selectedBuildkiteOrgId);
    }
    return buildkiteIntegrations.length === 1 ? buildkiteIntegrations[0] : undefined;
  }, [buildkiteIntegrations, state.selectedBuildkiteOrgId]);

  // Load repos when GitHub integration is selected
  React.useEffect(() => {
    if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && selectedGithubIntegration) {
      setIsLoadingRepos(true);
      onUpdate({ selectedIntegrationId: selectedGithubIntegration.id });

      const currentIntegrationId = selectedGithubIntegration.id;

      fetchQuery(environment, listGithubRepositoriesQuery, {
        integrationId: currentIntegrationId,
      })
        .toPromise()
        .then((result: unknown) => {
          if (currentIntegrationId !== selectedGithubIntegration?.id) return;
          const repos = (result as { listGithubRepositories: GithubRepo[] })?.listGithubRepositories || [];
          onUpdate({ githubRepos: repos });
          setIsLoadingRepos(false);
        })
        .catch((error) => {
          console.error("Error fetching GitHub repos:", error);
          setIsLoadingRepos(false);
        });
    }
  }, [state.capabilityType, selectedGithubIntegration, environment, onUpdate]);

  // Load workflows when repo is selected
  React.useEffect(() => {
    if (state.selectedGitHubRepo && selectedGithubIntegration) {
      const repo = state.githubRepos.find((r) => r.id === parseInt(state.selectedGitHubRepo, 10));
      if (!repo) return;

      setIsLoadingWorkflows(true);

      const currentRepoId = state.selectedGitHubRepo;
      const currentIntegrationId = selectedGithubIntegration.id;

      fetchQuery(environment, listGithubWorkflowsQuery, {
        integrationId: currentIntegrationId,
        repoFullName: repo.full_name,
      })
        .toPromise()
        .then((result: unknown) => {
          if (currentRepoId !== state.selectedGitHubRepo || currentIntegrationId !== selectedGithubIntegration?.id) return;
          const workflows = (result as { listGithubWorkflows: GithubWorkflow[] })?.listGithubWorkflows || [];
          onUpdate({ githubWorkflows: workflows });
          setIsLoadingWorkflows(false);
        })
        .catch((error) => {
          console.error("Error fetching GitHub workflows:", error);
          setIsLoadingWorkflows(false);
        });
    }
  }, [state.selectedGitHubRepo, selectedGithubIntegration, state.githubRepos, environment, onUpdate]);

  // Clear workflow when repo changes
  React.useEffect(() => {
    onUpdate({
      selectedGitHubWorkflow: "",
      githubWorkflowDefaultBranch: "main",
      githubWorkflows: [],
    });
  }, [state.selectedGitHubRepo, onUpdate]);

  // Fetch workflow inputs when workflow is selected
  React.useEffect(() => {
    if (state.selectedGitHubWorkflow && state.selectedGitHubRepo && selectedGithubIntegration) {
      const repo = state.githubRepos.find((r) => r.id === parseInt(state.selectedGitHubRepo, 10));
      const workflow = state.githubWorkflows.find((w) => w.id === parseInt(state.selectedGitHubWorkflow, 10));

      if (!repo || !workflow) return;

      const currentWorkflowId = state.selectedGitHubWorkflow;
      const currentRepoId = state.selectedGitHubRepo;
      const currentIntegrationId = selectedGithubIntegration.id;

      fetchQuery(environment, getGithubWorkflowInputsQuery, {
        integrationId: currentIntegrationId,
        repoFullName: repo.full_name,
        workflowId: workflow.id,
      })
        .toPromise()
        .then((result: unknown) => {
          if (
            currentWorkflowId !== state.selectedGitHubWorkflow ||
            currentRepoId !== state.selectedGitHubRepo ||
            currentIntegrationId !== selectedGithubIntegration?.id
          )
            return;

          const workflowInfo = (result as { getGithubWorkflowInputs: {
            error?: string;
            default_branch?: string;
            parameters_schema?: {
              properties?: Record<string, { type?: string; description?: string; default?: unknown; enum?: string[] }>;
              required?: string[];
            };
          } })?.getGithubWorkflowInputs;

          if (workflowInfo?.error) {
            toast({
              title: "Error",
              description: `Failed to fetch workflow inputs: ${workflowInfo.error}`,
              variant: "error",
            });
            return;
          }

          if (workflowInfo?.default_branch) {
            onUpdate({ githubWorkflowDefaultBranch: workflowInfo.default_branch });
          }

          if (workflowInfo?.parameters_schema) {
            const convertSchemaToParameters = (schema: {
              properties?: Record<string, { type?: string; description?: string; default?: unknown; enum?: string[] }>;
              required?: string[];
            }): Parameter[] => {
              const properties = schema.properties || {};
              const required = schema.required || [];

              return Object.entries(properties).map(([name, propSchema]) => {
                const param: Parameter = {
                  id: `workflow-${name}-${Date.now()}`,
                  name,
                  type: propSchema.enum ? "select" : (propSchema.type as Parameter["type"]) || "string",
                  description: propSchema.description || "",
                  required: required.includes(name),
                  locked: true,
                };

                if (propSchema.default !== undefined) {
                  param.defaultValue = String(propSchema.default);
                }

                if (propSchema.enum) {
                  param.selectOptions = propSchema.enum;
                }

                return param;
              });
            };

            const workflowParams = convertSchemaToParameters(workflowInfo.parameters_schema);
            const expandAll = (params: Parameter[]): string[] => {
              const ids: string[] = [];
              params.forEach((p) => {
                if (p.type === "object") {
                  ids.push(p.id);
                  if (p.properties) {
                    ids.push(...expandAll(p.properties));
                  }
                }
              });
              return ids;
            };
            onUpdate({
              parameters: workflowParams,
              expandedParams: new Set(expandAll(workflowParams)),
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching GitHub workflow inputs:", error);
          toast({
            title: "Error",
            description: `Failed to fetch workflow inputs: ${error.message}`,
            variant: "error",
          });
        });
    }
  }, [state.selectedGitHubWorkflow, state.selectedGitHubRepo, selectedGithubIntegration, state.githubRepos, state.githubWorkflows, environment, toast, onUpdate]);

  // Load Buildkite pipelines when integration is selected
  React.useEffect(() => {
    if (state.capabilityType === "BUILDKITE_PIPELINE" && selectedBuildkiteIntegration) {
      setIsLoadingPipelines(true);
      onUpdate({ selectedIntegrationId: selectedBuildkiteIntegration.id });

      const currentIntegrationId = selectedBuildkiteIntegration.id;

      fetchQuery(environment, listBuildkitePipelinesQuery, {
        integrationId: currentIntegrationId,
      })
        .toPromise()
        .then((result: unknown) => {
          if (currentIntegrationId !== selectedBuildkiteIntegration?.id) return;
          const pipelines = (result as { listBuildkitePipelines: BuildkitePipeline[] })?.listBuildkitePipelines || [];
          onUpdate({ buildkitePipelines: pipelines });
          setIsLoadingPipelines(false);
        })
        .catch((error) => {
          console.error("Error fetching Buildkite pipelines:", error);
          setIsLoadingPipelines(false);
        });
    }
  }, [state.capabilityType, selectedBuildkiteIntegration, environment, onUpdate]);

  // Fetch pipeline inputs when Buildkite pipeline is selected
  React.useEffect(() => {
    if (state.selectedBuildkitePipeline && selectedBuildkiteIntegration) {
      const pipeline = state.buildkitePipelines.find((p) => p.slug === state.selectedBuildkitePipeline);
      const orgSlug = selectedBuildkiteIntegration.buildkiteOrgSlug;

      if (!pipeline || !orgSlug) return;

      const currentPipelineSlug = state.selectedBuildkitePipeline;
      const currentIntegrationId = selectedBuildkiteIntegration.id;

      fetchQuery(environment, getBuildkitePipelineInputsQuery, {
        integrationId: currentIntegrationId,
        orgSlug: orgSlug,
        pipelineSlug: pipeline.slug,
      })
        .toPromise()
        .then((result: unknown) => {
          if (
            currentPipelineSlug !== state.selectedBuildkitePipeline ||
            currentIntegrationId !== selectedBuildkiteIntegration?.id
          )
            return;

          const pipelineInfo = (result as { getBuildkitePipelineInputs: {
            error?: string;
            parameters_schema?: {
              properties?: Record<string, { type?: string; description?: string; default?: unknown; enum?: string[]; items?: { enum?: string[] } }>;
              required?: string[];
            };
          } })?.getBuildkitePipelineInputs;

          if (pipelineInfo?.error) {
            toast({
              title: "Error",
              description: `Failed to fetch pipeline inputs: ${pipelineInfo.error}`,
              variant: "error",
            });
            return;
          }

          if (pipelineInfo?.parameters_schema) {
            const convertSchemaToParameters = (schema: {
              properties?: Record<string, { type?: string; description?: string; default?: unknown; enum?: string[]; items?: { enum?: string[] } }>;
              required?: string[];
            }): Parameter[] => {
              const properties = schema.properties || {};
              const required = schema.required || [];

              return Object.entries(properties).map(([name, propSchema]) => {
                const param: Parameter = {
                  id: `pipeline-${name}-${Date.now()}`,
                  name,
                  type: propSchema.type === "array" ? "select" : propSchema.enum ? "select" : (propSchema.type as Parameter["type"]) || "string",
                  description: propSchema.description || "",
                  required: required.includes(name),
                  locked: true,
                };

                if (propSchema.default !== undefined) {
                  if (Array.isArray(propSchema.default)) {
                    param.defaultValue = propSchema.default.join(", ");
                  } else {
                    param.defaultValue = String(propSchema.default);
                  }
                }

                if (propSchema.enum) {
                  param.selectOptions = propSchema.enum;
                } else if (propSchema.type === "array" && propSchema.items?.enum) {
                  param.selectOptions = propSchema.items.enum;
                }

                return param;
              });
            };

            const pipelineParams = convertSchemaToParameters(pipelineInfo.parameters_schema);
            const expandAll = (params: Parameter[]): string[] => {
              const ids: string[] = [];
              params.forEach((p) => {
                if (p.type === "object") {
                  ids.push(p.id);
                  if (p.properties) {
                    ids.push(...expandAll(p.properties));
                  }
                }
              });
              return ids;
            };
            onUpdate({
              parameters: pipelineParams,
              expandedParams: new Set(expandAll(pipelineParams)),
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching Buildkite pipeline inputs:", error);
          toast({
            title: "Error",
            description: `Failed to fetch pipeline inputs: ${error.message}`,
            variant: "error",
          });
        });
    }
  }, [state.selectedBuildkitePipeline, selectedBuildkiteIntegration, state.buildkitePipelines, environment, toast, onUpdate]);

  // Handle capability type change
  const handleCapabilityTypeChange = (type: CapabilityTypeOption) => {
    onUpdate({
      capabilityType: type,
      selectedGitHubOrgId: "",
      selectedGitHubRepo: "",
      selectedGitHubWorkflow: "",
      selectedBuildkiteOrgId: "",
      selectedBuildkitePipeline: "",
      githubRepos: [],
      githubWorkflows: [],
      buildkitePipelines: [],
      parameters: [],
      expandedParams: new Set(),
      hasSetDefaultName: false,
      name: "",
    });
  };

  const hasRequiredIntegration =
    (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && githubIntegrations.length > 0) ||
    (state.capabilityType === "BUILDKITE_PIPELINE" && buildkiteIntegrations.length > 0);

  return (
    <div className="space-y-6">
      {/* Type Selector */}
      <div>
        <Label htmlFor="type" className="mb-2 block text-sm font-medium text-muted-foreground">
          Type
        </Label>
        <Select
          value={state.capabilityType}
          onValueChange={(v) => handleCapabilityTypeChange(v as CapabilityTypeOption)}
        >
          <SelectTrigger className="bg-card border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(CAPABILITY_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                <div className="flex items-center gap-2">
                  {value === "GITHUB_ACTIONS_WORKFLOW" ? (
                    <GithubIcon className="h-4 w-4 text-muted-foreground" />
                  ) : value === "BUILDKITE_PIPELINE" ? (
                    <BuildkiteIcon className="h-4 w-4 text-muted-foreground" />
                  ) : null}
                  {label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!hasRequiredIntegration && (
        <div className="bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800/50 rounded-lg p-4">
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            No {CAPABILITY_TYPE_LABELS[state.capabilityType]} integration found. Please set up the integration in
            Settings first.
          </p>
        </div>
      )}

      {/* GitHub Actions Selectors */}
      {state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && hasRequiredIntegration && (
        <div className="space-y-4">
          <div className={`grid gap-4 ${githubIntegrations.length > 1 ? "grid-cols-3" : "grid-cols-2"}`}>
            {githubIntegrations.length > 1 && (
              <div>
                <Label htmlFor="github-org" className="mb-2 block text-sm font-medium text-muted-foreground">
                  GitHub Organization
                </Label>
                <GithubOrgTypeahead
                  orgs={githubIntegrations.map((integration) => ({
                    id: integration.id,
                    name: integration.githubAccountName || "Unknown",
                  }))}
                  value={state.selectedGitHubOrgId}
                  onChange={(value) => {
                    onUpdate({
                      selectedGitHubOrgId: value,
                      selectedGitHubRepo: "",
                      selectedGitHubWorkflow: "",
                      githubRepos: [],
                      githubWorkflows: [],
                      hasSetDefaultName: false,
                      name: "",
                    });
                  }}
                  placeholder="Select organization..."
                />
              </div>
            )}

            <div>
              <Label htmlFor="github-repo" className="mb-2 block text-sm font-medium text-muted-foreground">
                Repository
              </Label>
              <GithubRepoTypeahead
                repos={state.githubRepos}
                value={state.selectedGitHubRepo}
                onChange={(repoId) => {
                  onUpdate({
                    selectedGitHubRepo: repoId,
                    hasSetDefaultName: false,
                    name: "",
                  });
                }}
                isLoading={isLoadingRepos}
                disabled={githubIntegrations.length > 1 && !state.selectedGitHubOrgId}
                placeholder={
                  githubIntegrations.length > 1 && !state.selectedGitHubOrgId
                    ? "Select an organization first..."
                    : "Select a repository..."
                }
                emptyMessage={
                  githubIntegrations.length > 1 && !state.selectedGitHubOrgId
                    ? "Please specify the GitHub organization."
                    : "No repositories found."
                }
              />
            </div>

            <div>
              <Label htmlFor="github-workflow" className="mb-2 block text-sm font-medium text-muted-foreground">
                Workflow
              </Label>
              {state.selectedGitHubRepo && !isLoadingWorkflows && state.githubWorkflows.length === 0 ? (
                <WarningAlert>
                  No workflows found in this repository at ref:{" "}
                  {state.githubRepos.find((r) => r.id === parseInt(state.selectedGitHubRepo, 10))?.default_branch || "main"}
                </WarningAlert>
              ) : (
                <GithubWorkflowTypeahead
                  workflows={state.githubWorkflows}
                  value={state.selectedGitHubWorkflow}
                  onChange={(workflowId) => {
                    onUpdate({
                      selectedGitHubWorkflow: workflowId,
                      hasSetDefaultName: false,
                      name: "",
                    });
                  }}
                  isLoading={isLoadingWorkflows}
                  disabled={!state.selectedGitHubRepo}
                  placeholder={
                    !state.selectedGitHubRepo
                      ? "Select a repository first..."
                      : "Select a workflow..."
                  }
                  emptyMessage={
                    !state.selectedGitHubRepo
                      ? "Please specify the repository."
                      : "No workflows found."
                  }
                />
              )}
              {/* Helper text for workflow_dispatch requirement */}
              <p className="text-xs text-muted-foreground mt-2">
                Only workflows with a <code className="text-muted-foreground bg-card px-1 rounded">workflow_dispatch</code> trigger are supported.{" "}
                <a
                  href="https://docs.github.com/en/actions/using-workflows/events-that-trigger-workflows#workflow_dispatch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80"
                >
                  Learn more
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Buildkite Selectors */}
      {state.capabilityType === "BUILDKITE_PIPELINE" && hasRequiredIntegration && (
        <div className={`grid gap-4 ${buildkiteIntegrations.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {buildkiteIntegrations.length > 1 && (
            <div>
              <Label htmlFor="buildkite-org" className="mb-2 block text-sm font-medium text-muted-foreground">
                Buildkite Organization
              </Label>
              <Select
                value={state.selectedBuildkiteOrgId}
                onValueChange={(value) => {
                  onUpdate({
                    selectedBuildkiteOrgId: value,
                    selectedBuildkitePipeline: "",
                    buildkitePipelines: [],
                    hasSetDefaultName: false,
                    name: "",
                  });
                }}
              >
                <SelectTrigger className="bg-card border-border">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  {buildkiteIntegrations.map((integration) => (
                    <SelectItem key={integration.id} value={integration.id}>
                      {integration.buildkiteOrgSlug || "Unknown"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="buildkite-pipeline" className="mb-2 block text-sm font-medium text-muted-foreground">
              Pipeline
            </Label>
            <BuildkitePipelineTypeahead
              pipelines={state.buildkitePipelines}
              value={state.selectedBuildkitePipeline}
              onChange={(pipelineSlug) => {
                onUpdate({
                  selectedBuildkitePipeline: pipelineSlug,
                  hasSetDefaultName: false,
                  name: "",
                });
              }}
              isLoading={isLoadingPipelines}
              disabled={buildkiteIntegrations.length > 1 && !state.selectedBuildkiteOrgId}
              placeholder={
                buildkiteIntegrations.length > 1 && !state.selectedBuildkiteOrgId
                  ? "Select an organization first..."
                  : "Select a pipeline..."
              }
            />
          </div>
        </div>
      )}

    </div>
  );
}
